import { readonly, ref, type Ref } from "vue";

/**
 * Shared "is this a phone-width viewport" flag, matching Tailwind's `sm` breakpoint.
 *
 * Module-scope state and a single MediaQueryList listener, in the style of the Pinia stores here
 * (see stores/Permissions.ts): every consumer reads the same ref rather than each mounting its own
 * matchMedia. Several surfaces need to make a *structural* choice at this breakpoint rather than a
 * purely visual one — rendering a different control, or turning virtual scrolling off — and those
 * cannot be expressed as a `sm:` utility.
 *
 * Reach for a `sm:` class first. This is only for cases where the DOM itself has to differ.
 */

/** Keep in sync with Tailwind's `sm` breakpoint and with the media queries in index.css. */
export const COMPACT_VIEWPORT_QUERY = "(max-width: 639px)";

function initialMatch(): boolean {
    if ( typeof window === "undefined" || typeof window.matchMedia !== "function" ) {
        // happy-dom in unit tests, and any non-browser context. Defaulting to false preserves the
        // desktop rendering that existing specs were written against.
        return false;
    }
    return window.matchMedia( COMPACT_VIEWPORT_QUERY ).matches;
}

const isCompact = ref< boolean >( initialMatch() );

let mediaQuery: MediaQueryList | null = null;

function ensureListening(): void {
    if ( mediaQuery ) return;
    if ( typeof window === "undefined" || typeof window.matchMedia !== "function" ) return;

    mediaQuery = window.matchMedia( COMPACT_VIEWPORT_QUERY );
    isCompact.value = mediaQuery.matches;

    // Never torn down: the listener is one per document for the lifetime of the app, and removing
    // it when the last consumer unmounts would leave the next mount with a stale value until the
    // following resize.
    mediaQuery.addEventListener( "change", ( event: MediaQueryListEvent ) => {
        isCompact.value = event.matches;
    } );
}

export function useCompactViewport(): { isCompactViewport: Readonly< Ref< boolean > > } {
    ensureListening();
    return { isCompactViewport: readonly( isCompact ) };
}

/** Test seam: force a value and stop tracking the real viewport. */
export function __setCompactViewportForTests( value: boolean ): void {
    isCompact.value = value;
}
